/**
 * Settings Store - Pinia
 *
 * Central configuration and app settings management.
 * Migrated from VueX module src/store/modules/settings/
 *
 * This is the most complex store with:
 * - DB initialization logic
 * - Cross-store dependencies (mapHosts, activityTypes)
 * - Browser API permission checks
 */

import { defineStore } from 'pinia';
import { appDebugLog } from '@/utils/appDebugLog';
import GeoMoverDB from '@/db/geoMoverDB';
import { mapHostsData as seedMapHostsData } from '@/db/data/mapHostsData';

// Helper to normalize seed map hosts
function normalizeSeedMapHosts (seed) {
  if (!Array.isArray(seed)) return [];
  return seed.filter(h => typeof h?.id === 'number' && typeof h?.slug === 'string');
}

const POI_CREATE_HOLD_DURATION_DEFAULT_MS = 1000;
const POI_CREATE_HOLD_MOVE_TOLERANCE_DEFAULT_PX = 8;

function isBlankSettingValue (value) {
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}

function normalizePoiCreateHoldDurationMs (value) {
  if (isBlankSettingValue(value)) return POI_CREATE_HOLD_DURATION_DEFAULT_MS;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return POI_CREATE_HOLD_DURATION_DEFAULT_MS;
  return Math.min(3000, Math.max(300, numeric));
}

function normalizePoiCreateHoldMoveTolerancePx (value) {
  if (isBlankSettingValue(value)) return POI_CREATE_HOLD_MOVE_TOLERANCE_DEFAULT_PX;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return POI_CREATE_HOLD_MOVE_TOLERANCE_DEFAULT_PX;
  return Math.min(32, Math.max(2, numeric));
}

export const useSettingsStore = defineStore('settings', {
  state: () => ({
    vueAppVersion: null,
    dbReady: false,
    setupFinished: false,
    // Geolocation settings
    trackingAllowed: false,
    geolocationPermissionStatus: 'prompt',
    // Geolocation Retrieval Options
    enableHighAccuracy: false,
    maximumAge: 0,
    timeout: 5000,
    // Geolocation Recording constraints
    useMinSpeedThreshold: false,
    minSpeedThreshold: 0.9,
    useRecordingTimeInterval: false,
    recordingTimeInterval: 1500,
    // Live map updates mode: 'off' | 'auto' | 'always'
    liveUpdateMode: 'auto',
    // Notification
    showNotificationWhenNotMoving: true,
    // Screen Wake-Lock
    screenWakeLockAvailability: false,
    // Touch Screen Protection
    useTouchScreenProtection: false,
    touchScreenProtectionActive: false,
    // Reverse Geocoding
    addressService: 'none',
    // Zoom
    zoomLevelNear: 16,
    zoomLevelFar: 0,
    zoomLevelManual: undefined,
    // Fallback Location (for development without GPS)
    fallbackLatitude: 48.2082, // Vienna, Austria
    fallbackLongitude: 16.3738,
    // Pagination
    maxMapHostsPerPage: 0,
    maxNotesPerPage: 0,
    maxTrackedRoutesPerPage: 0,
    // Track Studio
    maxTracksToMerge: 10,
    maxTracksToOverlay: 10,
    maxRoutesForComparison: 10,
    restSpeedThresholdKmh: 1.0,
    restMinDurationSec: 120,
    // POI map interactions
    poiCreateHoldDurationMs: 1000,
    poiCreateHoldMoveTolerancePx: 8,
    // Speech Recognition
    useSpeechRecognition: false,
    speechRecognitionAvailable: false,
    speechRecognitionSelectedLanguage: 'en-US',
    availableLanguages: [
      { code: 'de-AT', label: 'de', commands: { stop: 'Kommando Aufnahme stoppen' } },
      { code: 'en-US', label: 'en', commands: { stop: 'Command stop recording' } }
    ],
    // Debugging
    console: false,
    debugging: false,
    log: [],
    // Selected Map Layer
    selectedMapLayer: 'OpenStreetMaps',
    saveAccidentallyInterruptedRecording: false
  }),

  getters: {
    getVueAppVersion: (state) => state.vueAppVersion,
    getDbReady: (state) => state.dbReady,
    getSetupFinished: (state) => state.setupFinished,
    getTrackingAllowed: (state) => state.trackingAllowed,
    getGeolocationPermissionStatus: (state) => state.geolocationPermissionStatus,
    getEnableHighAccuracy: (state) => state.enableHighAccuracy,
    getMaximumAge: (state) => state.maximumAge,
    getTimeout: (state) => state.timeout,
    getGeolocationSettings: (state) => ({
      enableHighAccuracy: state.enableHighAccuracy,
      maximumAge: state.maximumAge,
      timeout: state.timeout
    }),
    getUseMinSpeedThreshold: (state) => state.useMinSpeedThreshold,
    getMinSpeedThreshold: (state) => state.minSpeedThreshold,
    getUseRecordingTimeInterval: (state) => state.useRecordingTimeInterval,
    getRecordingTimeInterval: (state) => state.recordingTimeInterval,
    getLiveUpdateMode: (state) => state.liveUpdateMode,
    getShowNotificationWhenNotMoving: (state) => state.showNotificationWhenNotMoving,
    getScreenWakeLockAvailability: (state) => state.screenWakeLockAvailability,
    getUseTouchScreenProtection: (state) => state.useTouchScreenProtection,
    getTouchScreenProtectionActive: (state) => state.touchScreenProtectionActive,
    getAddressService: (state) => state.addressService,
    getZoomLevelNear: (state) => state.zoomLevelNear,
    getZoomLevelFar: (state) => state.zoomLevelFar,
    getZoomLevelManual: (state) => state.zoomLevelManual,
    getZoomLevelCurrent: (state) => state.zoomLevelManual === undefined ? state.zoomLevelFar : state.zoomLevelManual,
    getFallbackLatitude: (state) => state.fallbackLatitude,
    getFallbackLongitude: (state) => state.fallbackLongitude,
    getMaxMapHostsPerPage: (state) => state.maxMapHostsPerPage,
    getMaxNotesPerPage: (state) => state.maxNotesPerPage,
    getMaxTrackedRoutesPerPage: (state) => state.maxTrackedRoutesPerPage,
    getMaxTracksToMerge: (state) => state.maxTracksToMerge,
    getMaxTracksToOverlay: (state) => state.maxTracksToOverlay,
    getMaxRoutesForComparison: (state) => state.maxRoutesForComparison,
    getRestSpeedThresholdKmh: (state) => state.restSpeedThresholdKmh,
    getRestMinDurationSec: (state) => state.restMinDurationSec,
    getPoiCreateHoldDurationMs: (state) => state.poiCreateHoldDurationMs,
    getPoiCreateHoldMoveTolerancePx: (state) => state.poiCreateHoldMoveTolerancePx,
    getUseSpeechRecognition: (state) => state.useSpeechRecognition,
    getSpeechRecognitionAvailable: (state) => state.speechRecognitionAvailable,
    getSpeechRecognitionSelectedLanguage: (state) => state.speechRecognitionSelectedLanguage,
    getAvailableLanguages: (state) => state.availableLanguages,
    getConsole: (state) => state.console,
    getDebugging: (state) => state.debugging,
    getLog: (state) => state.log,
    getSelectedMapLayer: (state) => state.selectedMapLayer,
    getSaveAccidentallyInterruptedRecording: (state) => state.saveAccidentallyInterruptedRecording
  },

  actions: {
    setDbReady (bool) {
      this.dbReady = bool;
    },

    setSetupFinished (bool) {
      this.setupFinished = bool;
    },

    /**
     * Initialize settings from IndexedDB
     * This is the main initialization action called on app startup
     */
    async initSettings () {
      try {
        const data = await GeoMoverDB.getAllSettings();

        // Apply all settings from DB
        this.trackingAllowed = data.trackingAllowed;
        this.geolocationPermissionStatus = data.geolocationPermissionStatus;
        this.enableHighAccuracy = data.enableHighAccuracy;
        this.timeout = data.timeout;
        this.maximumAge = data.maximumAge;
        this.useMinSpeedThreshold = data.useMinSpeedThreshold;
        this.minSpeedThreshold = data.minSpeedThreshold;
        this.useRecordingTimeInterval = data.useRecordingTimeInterval;
        this.recordingTimeInterval = data.recordingTimeInterval;
        this.liveUpdateMode = data.liveUpdateMode || 'auto';
        this.showNotificationWhenNotMoving = data.showNotificationWhenNotMoving;
        this.screenWakeLockAvailability = data.screenWakeLockAvailability;
        this.useTouchScreenProtection = data.useTouchScreenProtection;
        this.touchScreenProtectionActive = data.touchScreenProtectionActive;
        this.addressService = data.addressService;
        this.zoomLevelNear = data.zoomLevelNear;
        this.zoomLevelFar = data.zoomLevelFar;
        this.fallbackLatitude = data.fallbackLatitude || 48.2082;
        this.fallbackLongitude = data.fallbackLongitude || 16.3738;
        this.maxMapHostsPerPage = data.maxMapHostsPerPage;
        this.maxNotesPerPage = data.maxNotesPerPage;
        this.maxTrackedRoutesPerPage = data.maxTrackedRoutesPerPage;
        this.maxTracksToMerge = data.maxTracksToMerge ?? 10;
        this.maxTracksToOverlay = data.maxTracksToOverlay ?? 10;
        this.maxRoutesForComparison = data.maxRoutesForComparison ?? 10;
        this.restSpeedThresholdKmh = data.restSpeedThresholdKmh ?? 1.0;
        this.restMinDurationSec = data.restMinDurationSec ?? 120;
        this.poiCreateHoldDurationMs = normalizePoiCreateHoldDurationMs(data.poiCreateHoldDurationMs);
        this.poiCreateHoldMoveTolerancePx = normalizePoiCreateHoldMoveTolerancePx(data.poiCreateHoldMoveTolerancePx);
        this.useSpeechRecognition = data.useSpeechRecognition;
        if (typeof data.speechRecognitionSelectedLanguage === 'string' && data.speechRecognitionSelectedLanguage) {
          this.speechRecognitionSelectedLanguage = data.speechRecognitionSelectedLanguage;
        }
        this.debugging = data.debugging;
        this.console = data.console;
        if (data.selectedMapLayer) {
          this.selectedMapLayer = data.selectedMapLayer;
        }

        // Cross-store: Update activityTypes store
        try {
          const { useActivityTypesStore } = await import('./activityTypes');
          const activityTypesStore = useActivityTypesStore();
          activityTypesStore.currentActivityType = data.currentActivityType;
        }
        catch {
          appDebugLog.debug('[Pinia] ActivityTypes store not yet available');
        }

        // Initialize map hosts from DB
        let allMapHosts = await GeoMoverDB.findAll('mapHosts', 'id', 'next');

        // Ensure newly added default Map Hosts are present
        const existingIds = new Set((allMapHosts || []).map(h => h?.id).filter(id => typeof id === 'number'));
        const missingSeedHosts = normalizeSeedMapHosts(seedMapHostsData).filter(h => !existingIds.has(h.id));

        if (missingSeedHosts.length) {
          const result = await GeoMoverDB.addMany('mapHosts', missingSeedHosts);
          if (result.inserted > 0) {
            allMapHosts = await GeoMoverDB.findAll('mapHosts', 'id', 'next');
          }
        }

        // Update mapHosts store (including service-only geosearch providers like Nominatim)
        const totalMapHosts = allMapHosts;

        try {
          const { useMapHostsStore } = await import('./mapHosts');
          const mapHostsStore = useMapHostsStore();
          mapHostsStore.mapHosts = totalMapHosts;

          // Set active map hosts
          const onlyActivatedMaps = totalMapHosts
            .filter(h => h.active && h.slug !== 'nominatim' && !h.serviceOnly)
            .map(h => ({ id: h.id, title: h.title, slug: h.slug }));
          mapHostsStore.activeMapHosts = onlyActivatedMaps;
        }
        catch {
          appDebugLog.debug('[Pinia] MapHosts store not yet available');
        }

        // Run Web API checks
        this.initialGeolocationPermissionCheck();
        this.initialScreenWakeLockAvailabilityCheck();

        return true;
      }
      catch (error) {
        appDebugLog.error('Pinia: initSettings error', error);
        return error;
      }
    },

    /**
     * Update a single setting in the database
     */
    async updateOneSetting (payload) {
      try {
        await GeoMoverDB.updateOneSetting(payload);
      }
      catch (error) {
        appDebugLog.error('updateOneSetting error', error);
      }
    },

    // Individual setting actions with DB persistence
    setTrackingAllowed (bool) {
      this.trackingAllowed = bool;
      this.updateOneSetting({ feature: 'trackingAllowed', value: bool });
    },

    initialGeolocationPermissionCheck () {
      navigator.permissions.query({ name: 'geolocation' })
        .then(status => this.reportGeolocationPermissionStatus(status.state));
    },

    initialScreenWakeLockAvailabilityCheck () {
      this.reportScreenWakeLockAvailability('wakeLock' in navigator);
    },

    handleGeolocationPermission () {
      navigator.permissions.query({ name: 'geolocation' })
        .then(status => {
          if (status.state === 'prompt' || status.state === 'denied') {
            navigator.geolocation.getCurrentPosition(() => {});
          }
          this.reportGeolocationPermissionStatus(status.state);
          status.onchange = () => this.reportGeolocationPermissionStatus(status.state);
        });
    },

    reportGeolocationPermissionStatus (status) {
      this.geolocationPermissionStatus = status;
      this.updateOneSetting({ feature: 'geolocationPermissionStatus', value: status });
    },

    reportScreenWakeLockAvailability (status) {
      this.screenWakeLockAvailability = status;
      this.updateOneSetting({ feature: 'screenWakeLockAvailability', value: status });
    },

    setEnableHighAccuracy (bool) {
      this.enableHighAccuracy = bool;
      this.updateOneSetting({ feature: 'enableHighAccuracy', value: bool });
    },

    setMaximumAge (int) {
      this.maximumAge = parseInt(int);
      this.updateOneSetting({ feature: 'maximumAge', value: parseInt(int) });
    },

    setTimeout (int) {
      this.timeout = parseInt(int);
      this.updateOneSetting({ feature: 'timeout', value: parseInt(int) });
    },

    setUseMinSpeedThreshold (bool) {
      this.useMinSpeedThreshold = bool;
      this.updateOneSetting({ feature: 'useMinSpeedThreshold', value: bool });
    },

    setMinSpeedThreshold (float) {
      this.minSpeedThreshold = parseFloat(float);
      this.updateOneSetting({ feature: 'minSpeedThreshold', value: parseFloat(float) });
    },

    setUseRecordingTimeInterval (bool) {
      this.useRecordingTimeInterval = bool;
      this.updateOneSetting({ feature: 'useRecordingTimeInterval', value: bool });
    },

    setRecordingTimeInterval (int) {
      this.recordingTimeInterval = parseInt(int);
      this.updateOneSetting({ feature: 'recordingTimeInterval', value: parseInt(int) });
    },

    setLiveUpdateMode (mode) {
      if (!['off', 'auto', 'always'].includes(mode)) mode = 'auto';
      this.liveUpdateMode = mode;
      this.updateOneSetting({ feature: 'liveUpdateMode', value: mode });
    },

    setShowNotificationWhenNotMoving (bool) {
      this.showNotificationWhenNotMoving = bool;
      this.updateOneSetting({ feature: 'showNotificationWhenNotMoving', value: bool });
    },

    setScreenWakeLockAvailability (bool) {
      this.screenWakeLockAvailability = bool;
      this.updateOneSetting({ feature: 'screenWakeLockAvailability', value: bool });
    },

    setUseTouchScreenProtection (bool) {
      this.useTouchScreenProtection = bool;
      this.updateOneSetting({ feature: 'useTouchScreenProtection', value: bool });
    },

    setTouchScreenProtectionActive (bool) {
      this.touchScreenProtectionActive = bool;
      this.updateOneSetting({ feature: 'touchScreenProtectionActive', value: bool });
    },

    setAddressService (payload) {
      this.addressService = payload;
      this.updateOneSetting({ feature: 'addressService', value: payload });
    },

    setZoomLevelNear (int) {
      this.zoomLevelNear = parseInt(int);
      this.updateOneSetting({ feature: 'zoomLevelNear', value: parseInt(int) });
    },

    setZoomLevelFar (int) {
      this.zoomLevelFar = parseInt(int);
      this.updateOneSetting({ feature: 'zoomLevelFar', value: parseInt(int) });
    },

    setZoomLevelManual (int) {
      this.zoomLevelManual = parseInt(int);
      // Not persisted to IDB
    },

    setFallbackLatitude (float) {
      this.fallbackLatitude = parseFloat(float);
      this.updateOneSetting({ feature: 'fallbackLatitude', value: parseFloat(float) });
    },

    setFallbackLongitude (float) {
      this.fallbackLongitude = parseFloat(float);
      this.updateOneSetting({ feature: 'fallbackLongitude', value: parseFloat(float) });
    },

    setMaxMapHostsPerPage (int) {
      this.maxMapHostsPerPage = parseInt(int);
      this.updateOneSetting({ feature: 'maxMapHostsPerPage', value: parseInt(int) });
    },

    setMaxNotesPerPage (int) {
      this.maxNotesPerPage = parseInt(int);
      this.updateOneSetting({ feature: 'maxNotesPerPage', value: parseInt(int) });
    },

    setMaxTrackedRoutesPerPage (int) {
      this.maxTrackedRoutesPerPage = parseInt(int);
      this.updateOneSetting({ feature: 'maxTrackedRoutesPerPage', value: parseInt(int) });
    },

    setMaxTracksToMerge (int) {
      this.maxTracksToMerge = parseInt(int);
      this.updateOneSetting({ feature: 'maxTracksToMerge', value: parseInt(int) });
    },

    setMaxTracksToOverlay (int) {
      this.maxTracksToOverlay = parseInt(int);
      this.updateOneSetting({ feature: 'maxTracksToOverlay', value: parseInt(int) });
    },

    setMaxRoutesForComparison (int) {
      this.maxRoutesForComparison = parseInt(int);
      this.updateOneSetting({ feature: 'maxRoutesForComparison', value: parseInt(int) });
    },

    setRestSpeedThresholdKmh (float) {
      this.restSpeedThresholdKmh = parseFloat(float);
      this.updateOneSetting({ feature: 'restSpeedThresholdKmh', value: parseFloat(float) });
    },

    setRestMinDurationSec (int) {
      this.restMinDurationSec = parseInt(int);
      this.updateOneSetting({ feature: 'restMinDurationSec', value: parseInt(int) });
    },

    setPoiCreateHoldDurationMs (int) {
      const normalized = normalizePoiCreateHoldDurationMs(int);
      this.poiCreateHoldDurationMs = normalized;
      this.updateOneSetting({ feature: 'poiCreateHoldDurationMs', value: normalized });
    },

    setPoiCreateHoldMoveTolerancePx (int) {
      const normalized = normalizePoiCreateHoldMoveTolerancePx(int);
      this.poiCreateHoldMoveTolerancePx = normalized;
      this.updateOneSetting({ feature: 'poiCreateHoldMoveTolerancePx', value: normalized });
    },

    setUseSpeechRecognition (bool) {
      this.useSpeechRecognition = bool;
      this.updateOneSetting({ feature: 'useSpeechRecognition', value: bool });
    },

    setSpeechRecognitionAvailable (bool) {
      this.speechRecognitionAvailable = bool;
    },

    setSpeechRecognitionSelectedLanguage (langCode) {
      if (typeof langCode !== 'string') return;
      const trimmed = langCode.trim();
      if (!trimmed) return;

      const availableCodes = new Set((this.availableLanguages || []).map(l => l?.code).filter(Boolean));
      const next = availableCodes.has(trimmed) ? trimmed : this.speechRecognitionSelectedLanguage;

      this.speechRecognitionSelectedLanguage = next;
      this.updateOneSetting({ feature: 'speechRecognitionSelectedLanguage', value: next });
    },

    setConsole (bool) {
      this.console = bool;
      this.updateOneSetting({ feature: 'console', value: bool });
    },

    setDebugging (bool) {
      this.debugging = bool;
      this.updateOneSetting({ feature: 'debugging', value: bool });
    },

    /**
     * Push a log entry to the bounded ring buffer (max 200 entries).
     * @param {{ type: string, title: string, text: string, val?: any }} entry
     */
    pushLog (entry) {
      if (!this.debugging) return;
      this.log.push({
        ts: Date.now(),
        type: entry.type || 'log',
        title: entry.title || '',
        text: entry.text || '',
        val: entry.val
      });
      // Bounded ring buffer: keep only last 200 entries
      if (this.log.length > 200) this.log.shift();
    },

    resetLog () {
      this.log = [];
    },

    setSelectedMapLayer (layerName) {
      this.selectedMapLayer = layerName;
      this.updateOneSetting({ feature: 'selectedMapLayer', value: layerName });
    },

    /**
     * Apply settings from a preset object
     * @param {Object} preset - The preset containing geolocation settings
     */
    applyPresetSettings (preset) {
      if (!preset) return;

      // Apply geolocation retrieval settings
      if (preset.enableHighAccuracy !== undefined) {
        this.setEnableHighAccuracy(preset.enableHighAccuracy);
      }
      if (preset.maximumAge !== undefined) {
        this.setMaximumAge(preset.maximumAge);
      }
      if (preset.timeout !== undefined) {
        this.setTimeout(preset.timeout);
      }

      // Apply recording constraints
      if (preset.useMinSpeedThreshold !== undefined) {
        this.setUseMinSpeedThreshold(preset.useMinSpeedThreshold);
      }
      if (preset.minSpeedThreshold !== undefined) {
        this.setMinSpeedThreshold(preset.minSpeedThreshold);
      }
      if (preset.useRecordingTimeInterval !== undefined) {
        this.setUseRecordingTimeInterval(preset.useRecordingTimeInterval);
      }
      if (preset.recordingTimeInterval !== undefined) {
        this.setRecordingTimeInterval(preset.recordingTimeInterval);
      }

      // Apply notification settings
      if (preset.showNotificationWhenNotMoving !== undefined) {
        this.setShowNotificationWhenNotMoving(preset.showNotificationWhenNotMoving);
      }

      // Apply keep-alive helpers
      if (preset.useTouchScreenProtection !== undefined) {
        this.setUseTouchScreenProtection(preset.useTouchScreenProtection);
      }
      if (preset.screenWakeLockAvailability !== undefined) {
        this.setScreenWakeLockAvailability(preset.screenWakeLockAvailability);
      }

      // Apply zoom settings
      if (preset.zoomLevelNear !== undefined) {
        this.setZoomLevelNear(preset.zoomLevelNear);
      }
      if (preset.zoomLevelFar !== undefined) {
        this.setZoomLevelFar(preset.zoomLevelFar);
      }

      appDebugLog.log('[Settings] Applied preset settings:', preset.presetName);
    }
  }
});
